import { Button } from '@1trading/react/components/ui/button';
import { GradientIcon } from '@1trading/react/components/ui/gradient-icon';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { Ban } from 'lucide-react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import LogoutButton from '@/components/ui/logout-button';
import { PageHeading, PageHeadingDescription, PageHeadingTitle } from '@/components/ui/page-heading';
import { type LdFeatureFlags, staticFeatureFlags } from '@/config/features';
import { OnboardingProgressStep, useProgress } from '@/context/progress';
import FcaQuestionnaireLayout from '@/features/onboarding/fca-questionnaire/layout';

const FcaQuestionnaireIneligible = () => {
  const { t } = useTranslation();

  return (
    <>
      <PageHeading centered>
        <PageHeadingTitle>{t('FCAQuestionnaireFailed.title')}</PageHeadingTitle>
        <PageHeadingDescription>{t('FCAQuestionnaireFailed.description')}</PageHeadingDescription>
      </PageHeading>
      <footer className="flex flex-col gap-4">
        <Button size="lg" variant="primary" asChild>
          <a href={import.meta.env.VITE_APP_SUPPORT_NEW_REQUEST_URL}>{t('contactSupport')}</a>
        </Button>
        <LogoutButton size="lg" variant="outline" />
      </footer>
    </>
  );
};
FcaQuestionnaireIneligible.displayName = 'FcaQuestionnaireIneligible';

const FcaQuestionnaireIneligibleFutures = () => {
  const { t } = useTranslation();

  return (
    <>
      <PageHeading centered>
        <PageHeadingTitle>{t('FCAQuestionnaireFailed.title')}</PageHeadingTitle>
        <PageHeadingDescription>{t('FCAQuestionnaireFailed.futures.description')}</PageHeadingDescription>
        <PageHeadingTitle className="!mt-8 text-lg md:text-2xl" asChild>
          <h2>{t('FCAQuestionnaireFailed.futures.title')}</h2>
        </PageHeadingTitle>
      </PageHeading>
      <footer className="flex flex-col gap-4 !mt-6">
        <Button size="lg" variant="primary" asChild>
          <Link to="/futures">{t('FCAQuestionnaireFailed.futures.actions.signUp')}</Link>
        </Button>
        <LogoutButton variant="link">{t('FCAQuestionnaireFailed.futures.actions.noThanks')}</LogoutButton>
      </footer>
    </>
  );
};
FcaQuestionnaireIneligibleFutures.displayName = 'FcaQuestionnaireIneligibleFutures';

const FcaQuestionnaireIneligiblePage = () => {
  const { setCurrentStep } = useProgress();
  const { futuresEnabled, futuresPublicTrading } = useFlags<LdFeatureFlags>();
  const isFuturesEnabledWithPublicTradingEnabled = futuresEnabled && futuresPublicTrading.enabled;
  const showFuturesFcaIneligibleFlow = isFuturesEnabledWithPublicTradingEnabled && staticFeatureFlags.FUTURES_FCA_INELIGIBLE_FLOW_ENABLED;

  useEffect(() => {
    return setCurrentStep(OnboardingProgressStep.PERSONAL_INFORMATION, 1);
  }, [setCurrentStep]);

  return (
    <FcaQuestionnaireLayout className="-md:justify-center space-y-8" size="sm">
      <GradientIcon className="size-32 mx-auto" strokeWidth={1.5}>
        <Ban />
      </GradientIcon>
      {showFuturesFcaIneligibleFlow ? <FcaQuestionnaireIneligibleFutures /> : <FcaQuestionnaireIneligible />}
    </FcaQuestionnaireLayout>
  );
};
FcaQuestionnaireIneligiblePage.displayName = 'FcaQuestionnaireIneligiblePage';

export default FcaQuestionnaireIneligiblePage;
